import { SignUpRequestExpanded } from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { useCreateUserAndRetailer } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useCreateUserAndRetailer";
import { useIncomeRanges } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useIncomeRanges";
import { useSignUpRequest } from "@faire/retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { useSaveRequestAndGoToNextStep } from "@faire/retailer-visitor-shared/views/SignUp/signupStepper";
import { Step } from "@faire/retailer-visitor-shared/views/SignUp/Step";
import { ISignUpRequest } from "@faire/web-api--source/indigofair/data/ISignUpRequest";
import { useCallback } from "react";

export const useSignupAndGoToNextStep = (currentStep: Step) => {
  const {
    createUserAndRetailer,
    userErrorMessage: signupError,
    isLoading: isSigningUp,
  } = useCreateUserAndRetailer();
  const { saveAndGoToNextStep } = useSaveRequestAndGoToNextStep(currentStep);
  const [{ country, annual_sales }] = useSignUpRequest([
    "country",
    "annual_sales",
  ]);

  const {
    incomeRangesOptions,
    isLoadingIncomeRanges: isFetching,
    incomeRangeForKey,
  } = useIncomeRanges(country);
  const signupAndGoToNext = useCallback(
    async (values: Partial<SignUpRequestExpanded>, skipSignup: boolean) => {
      if (skipSignup) {
        saveAndGoToNextStep(values);
        return;
      }

      const minimumAnnualSales = ISignUpRequest.IAnnualSales.build({
        ...incomeRangeForKey(incomeRangesOptions[0]?.value ?? ""),
      });

      const payload = {
        ...values,
        annual_sales: values.annual_sales ?? annual_sales ?? minimumAnnualSales,
      };

      const response = await createUserAndRetailer(payload);
      if (response) {
        saveAndGoToNextStep(payload, { retailer: response.retailer });
      }
    },
    [
      createUserAndRetailer,
      incomeRangeForKey,
      incomeRangesOptions,
      saveAndGoToNextStep,
      annual_sales,
    ]
  );

  return {
    isFetching,
    isSigningUp,
    signupError,
    signupAndGoToNext,
  };
};
